import { useState, useEffect } from "react";
import { Map } from "@vis.gl/react-google-maps";
import { Task } from "@/api.types";
import { TaskGroupMarkers } from "@/features/tasks/task-group-marker";
import { TasksSelector } from "@/features/tasks/tasks-selector";
import { SelectedTasksList } from "@/features/tasks/selected-tasks-list";
import { AllTripsDirections } from "@/features/trips/all-trips-directions";
import { TripWaypointsMarkers } from "@/features/trips/trip-waypoints-markers";
import { AllOperatorsLocations } from "@/features/operators/all-operators-locations";
import { Shortcuts } from "@/screens/dashboard/shortcuts";
import { useShortcutsStore } from "@/screens/dashboard/shortcuts.store";
import { useTasks, taskSearchQueries } from "@/api/tasks";
import { useOrderSearchStore } from "@/features/orders/order-search-store";
import { TripRouteMarkers } from "@/features/trips/trip-route-markers";
import { DepotsLayer } from "@/features/depots/depots-layer";

export function MapComponent() {
  const [selectedTasks, setSelectedTasks] = useState<Task[]>([]);
  const [showSelectedTasks, setShowSelectedTasks] = useState(true);
  const defaultCenter = { lat: 43.6532, lng: -79.3832 };
  const [zoomLevel, setZoomLevel] = useState(13);

  // Get state from shortcuts store
  const { isSelectingMode, setIsSelectingMode } = useShortcutsStore();

  // Effect to show task list when selecting mode is activated
  useEffect(() => {
    if (isSelectingMode) {
      setShowSelectedTasks(true);
    }
  }, [isSelectingMode]);

  // Order search store values
  const store = useOrderSearchStore();
  const searchedOrderNumber = store.searchedOrderNumber;
  const matchCount = store.matchCount;

  // Fetch tasks with unlimited results
  const { data: pickupsResponse } = useTasks(
    taskSearchQueries.activePickupTasks
  );
  const { data: deliveriesResponse } = useTasks(
    taskSearchQueries.activeDeliveryTasks
  );

  const unassignedPickupTasksCount =
    pickupsResponse?.data?.filter((task) => !task.tripId).length ?? 0;

  const unassignedDeliveryTasksCount =
    deliveriesResponse?.data?.filter((task) => !task.tripId).length ?? 0;

  // Update the selection handler to receive both bounds and tasks
  const handleSelectionComplete = (
    _: google.maps.LatLngBounds,
    selectedTasks: Task[]
  ) => {
    setSelectedTasks(selectedTasks);
    setIsSelectingMode(false);
  };

  return (
    <div className="absolute inset-0">
      <Shortcuts
        unassignedPickupTasksCount={unassignedPickupTasksCount}
        unassignedDeliveryTasksCount={unassignedDeliveryTasksCount}
      />

      {/* Search status indicator (only visible when searching) */}
      {searchedOrderNumber && (
        <div className="absolute top-4 left-4 z-10">
          <div className="bg-white/90 backdrop-blur-sm p-2 rounded-lg shadow-lg">
            <div className={matchCount > 0 ? "text-green-700" : "text-red-600"}>
              {matchCount > 0 ? (
                <div className="flex items-center font-medium">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>
                    Found {matchCount} tasks for Order #{searchedOrderNumber}
                  </span>
                </div>
              ) : (
                <div className="flex items-center font-medium">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <span>No tasks found for Order #{searchedOrderNumber}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Update the SelectedTasksList rendering with showSelectedTasks condition */}
      {showSelectedTasks && selectedTasks.length > 0 && (
        <div className="absolute right-4 bottom-16 z-10 max-h-[70vh] overflow-y-auto bg-white/95 rounded-lg shadow-lg">
          <SelectedTasksList
            tasks={selectedTasks}
            onClose={() => setShowSelectedTasks(false)}
          />
        </div>
      )}

      <Map
        defaultCenter={defaultCenter}
        defaultZoom={13}
        gestureHandling={"greedy"}
        disableDefaultUI={true}
        mapId={"3667d2e0a1e93d2b"}
        style={{ width: "100%", height: "100%" }}
        onZoomChanged={(event) => {
          setZoomLevel(event.detail.zoom);
        }}
      >
        <AllTripsDirections />
        <TaskGroupMarkers zoomLevel={zoomLevel} selectedTasks={selectedTasks} />
        <DepotsLayer zoomLevel={zoomLevel} />
        <AllOperatorsLocations zoomLevel={zoomLevel} />
        <TripWaypointsMarkers zoomLevel={zoomLevel} />
        <TripRouteMarkers zoomLevel={zoomLevel} />

        {isSelectingMode && (
          <TasksSelector onSelectionComplete={handleSelectionComplete} />
        )}
      </Map>
    </div>
  );
}
